// Boot do app: carrega o sql.js vendorizado, abre ou cria o arquivo .db,
// aplica as migrações, monta a casca e roteia por hash.

import type { Database, SqlJsStatic } from "sql.js";
import { createRepository, migrate } from "../persistence/sqljs-adapter.ts";
import type { Repository } from "../domain/ports.ts";
import { emptySnapshot, type Snapshot } from "../domain/snapshot.ts";
import { aplicarSementes } from "../domain/seed.ts";
import { apiEu, apiExecutar, apiLogout, apiSnapshot, euIdAtual } from "./api.ts";
import { podeLerModulo } from "../comandos/_acesso.ts";
import type { Modulo } from "../domain/schema.ts";
import { telaLogin } from "./login.ts";
import { botao } from "./ui.ts";
import { criarCliente } from "../domain/clientes.ts";
import { criarTipoPeca } from "../domain/tipos-peca.ts";
import { definirPrecoBase, definirPrecoCliente } from "../domain/precificacao.ts";
import { adicionarParada } from "../domain/paradas.ts";
import { definirRecebida, lancarLote } from "../domain/lotes.ts";
import { registrarDevolucao } from "../domain/devolucao.ts";
import {
  adicionarAjuste,
  criarFaturaRascunho,
  emitirFatura,
} from "../domain/faturamento.ts";
import { criarCategoriaFinanceira } from "../domain/categorias-financeiras.ts";
import { criarConta, registrarPagamento } from "../domain/contas.ts";
import { criarResponsavel, registrarRetirada } from "../domain/retiradas.ts";
import { hojeISO, paraISO, somarDias } from "../domain/datas.ts";
import { mountShell, type Shell } from "./shell.ts";
import { createStore } from "./store.ts";
import { toastErro, toastOk } from "./toast.ts";
import { SCREENS } from "./screens.ts";
import { allScreenIds, telaInicial } from "./nav.ts";
import { type Workspace, LocalWorkspace } from "./workspace.ts";

declare global {
  interface Window {
    initSqlJs?: (config?: {
      locateFile?: (file: string) => string;
    }) => Promise<SqlJsStatic>;
  }
}

const root = document.getElementById("app")!;

function gate(message: string, actions: { label: string; run: () => void }[]): void {
  root.textContent = "";
  const box = document.createElement("div");
  box.className = "gate";
  const logo = document.createElement("img");
  logo.src = "assets/logo-light.svg";
  logo.alt = "Arco-Íris Lavanderia";
  logo.className = "gate-logo";
  const p = document.createElement("p");
  p.textContent = message;
  box.append(logo, p);
  actions.forEach((a, i) => {
    // a primeira ação é a principal; as outras, alternativas
    const b = botao(i === 0 ? "primario" : "secundario", {}, a.label);
    b.addEventListener("click", a.run);
    box.append(b);
  });
  root.append(box);
}

async function loadSqlJs(): Promise<SqlJsStatic> {
  if (!window.initSqlJs) throw new Error("sql.js (vendor/sql-asm.js) não carregou.");
  return window.initSqlJs();
}

async function seedDefaults(repo: Repository): Promise<void> {
  const atual = repo.load();
  const semeado = aplicarSementes(atual);
  if (semeado !== atual) await repo.save(semeado);
}

// Dados fictícios só para o modo ?dev, para as telas terem o que mostrar.
async function seedDemo(repo: Repository): Promise<void> {
  let s = repo.load();
  if (s.clientes.length > 0) return;
  const cat = s.categorias.find((c) => c.nome === "Banho") ?? s.categorias[0];
  const catCama = s.categorias.find((c) => c.nome === "Cama") ?? cat;
  const comPreco = (nome: string, categoriaId: string, centavos?: number): void => {
    s = criarTipoPeca(s, { nome, categoriaId });
    if (centavos != null) {
      s = definirPrecoBase(s, s.tiposPeca[s.tiposPeca.length - 1]!.id, centavos, "2026-01-01");
    }
  };
  if (cat) {
    comPreco("Toalha de banho", cat.id); // ganha histórico mais abaixo
    comPreco("Toalha de rosto", cat.id, 150);
  }
  if (catCama) {
    comPreco("Lençol casal", catCama.id, 400);
    comPreco("Fronha", catCama.id, 120);
    comPreco("Edredom (a precificar)", catCama.id);
  }
  s = criarCliente(s, {
    nome: "Hotel Central Plaza",
    endereco: "Av. Paulista, 1000",
    contato: "(11) 3000-0000",
    codigo: "hcp",
    tipo: "Hotel",
    baseFaturamento: "saida",
    cicloFechamento: { tipo: "mensal" },
    devolucaoNoMesmoDia: false,
    diasVencimento: [],
  });
  s = criarCliente(s, {
    nome: "Pousada Vista Mar",
    endereco: "Rua da Praia, 50",
    contato: "(48) 99999-0000",
    codigo: "pvm",
    tipo: "Pousada",
    baseFaturamento: "entrada",
    cicloFechamento: { tipo: "quinzenal" },
    devolucaoNoMesmoDia: false,
    diasVencimento: [],
  });
  const hcp = s.clientes.find((c) => c.codigo === "HCP")!;
  s = adicionarParada(s, hcp.id, { diasSemana: [2, 4], horario: "16:00", acao: "coletar" });
  s = adicionarParada(s, hcp.id, { diasSemana: [3, 5], horario: "12:00", acao: "devolver" });

  // OS de exemplo
  const paradaHcp = s.paradas.find(
    (p) => p.clienteId === hcp.id && p.acao === "coletar",
  )!;
  const pvmId = s.clientes.find((c) => c.codigo === "PVM")!.id;
  s = lancarLote(s, {
    clienteId: hcp.id,
    os: 1044,
    coletaData: hojeISO(),
    coletaHora: "16:00",
    paradaId: paradaHcp.id,
    sacos: 4,
    urgente: false,
    relavagem: false,
    previstoManual: null,
  });
  const lote1 = s.lotes[s.lotes.length - 1]!.id;
  for (const [nome, q] of [
    ["Toalha de banho", 40],
    ["Fronha", 60],
    ["Lençol casal", 30],
  ] as const) {
    const t = s.tiposPeca.find((x) => x.nome === nome);
    if (t) s = definirRecebida(s, lote1, t.id, q);
  }
  s = lancarLote(s, {
    clienteId: pvmId,
    os: 1045,
    coletaData: hojeISO(),
    coletaHora: "08:30",
    paradaId: null,
    sacos: 2,
    urgente: true,
    relavagem: false,
    previstoManual: `${hojeISO()} 18:00`,
  });

  // uma OS antiga já devolvida, que deixou peças no backlog do hotel
  const ontem = paraISO(new Date(Date.now() - 86_400_000 * 6));
  s = lancarLote(s, {
    clienteId: hcp.id,
    os: 1039,
    coletaData: ontem,
    coletaHora: "16:00",
    paradaId: paradaHcp.id,
    sacos: 3,
    urgente: false,
    relavagem: false,
    previstoManual: null,
  });
  const anterior = s.lotes[s.lotes.length - 1]!.id;
  const tipoDe = (nome: string): string =>
    s.tiposPeca.find((x) => x.nome === nome)!.id;
  s = definirRecebida(s, anterior, tipoDe("Lençol casal"), 32);
  s = definirRecebida(s, anterior, tipoDe("Toalha de banho"), 25);
  s = registrarDevolucao(s, anterior, {
    data: paraISO(new Date(Date.now() - 86_400_000 * 5)),
    linhas: [
      {
        tipoId: tipoDe("Lençol casal"),
        devolvida: 29,
        retidas: [
          { motivo: "Relavagem", quantidade: 2 },
          { motivo: "Não terminou de passar", quantidade: 1 },
        ],
      },
      {
        tipoId: tipoDe("Toalha de banho"),
        devolvida: 23,
        retidas: [{ motivo: "Relavagem", quantidade: 1 }],
      },
    ],
  });

  // histórico de preços de exemplo (para a seção "Histórico de preços")
  const toalha = s.tiposPeca.find((t) => t.nome === "Toalha de banho");
  if (toalha) {
    s = definirPrecoBase(s, toalha.id, 220, "2026-01-01");
    s = definirPrecoBase(s, toalha.id, 250, "2026-07-01");
    s = definirPrecoCliente(s, hcp.id, toalha.id, 200, "2026-03-01");
    const pvm = s.clientes.find((c) => c.codigo === "PVM")!;
    s = definirPrecoCliente(s, pvm.id, toalha.id, 280, "2026-02-01");
  }
  s = definirPrecoBase(s, tipoDe("Lençol casal"), 400, "2026-01-01");
  s = definirPrecoBase(s, tipoDe("Fronha"), 120, "2026-01-01");

  // fatura de exemplo cobrindo a devolução da OS 1039
  s = criarFaturaRascunho(s, hcp.id, {
    de: paraISO(new Date(Date.now() - 86_400_000 * 20)),
    ate: hojeISO(),
  });
  s = adicionarAjuste(s, s.faturas[0]!.id, {
    descricao: "Cortesia (primeira remessa)",
    valorCentavos: -1500,
  });
  s = emitirFatura(s, s.faturas[0]!.id, hojeISO());

  // --- exemplos do Financeiro (Contas, Retiradas) ---
  s = criarCategoriaFinanceira(s, {
    nome: "Utilidades",
    subcategoria: "Energia elétrica",
    aplicaA: "pagar",
  });
  s = criarCategoriaFinanceira(s, {
    nome: "Insumos",
    subcategoria: "Produtos químicos",
    aplicaA: "pagar",
  });
  const catEnergia = s.categoriasFinanceiras.find(
    (c) => c.subcategoria === "Energia elétrica",
  )!;
  s = criarConta(s, {
    direcao: "pagar",
    descricao: "Energia elétrica — competência do mês",
    avulsoCentavos: 187430,
    vencimento: paraISO(new Date(Date.now() + 86_400_000 * 8)),
    categoriaId: catEnergia.id,
    fonte: null,
    clienteId: null,
    faturaId: null,
  });
  const catQuimicos = s.categoriasFinanceiras.find(
    (c) => c.subcategoria === "Produtos químicos",
  )!;
  s = criarConta(s, {
    direcao: "pagar",
    descricao: "Fornecedor de sabão e alvejante",
    avulsoCentavos: 92000,
    vencimento: paraISO(new Date(Date.now() - 86_400_000 * 2)),
    categoriaId: catQuimicos.id,
    fonte: null,
    clienteId: null,
    faturaId: null,
  });
  const ctaParcial = s.contas[s.contas.length - 1]!.id;
  s = registrarPagamento(s, ctaParcial, {
    data: hojeISO(),
    valorCentavos: 40000,
  });

  s = historicoDemo(s);

  s = criarResponsavel(s, { nome: "Alexandre", limiteMensalCentavos: 500000 });
  s = criarResponsavel(s, { nome: "Sogro", limiteMensalCentavos: null });
  const socio1 = s.responsaveis[0]!.id;
  s = registrarRetirada(s, {
    responsavelId: socio1,
    data: paraISO(new Date(Date.now() - 86_400_000 * 10)),
    valorCentavos: 150000,
  });
  s = registrarRetirada(s, {
    responsavelId: socio1,
    data: hojeISO(),
    valorCentavos: 80000,
  });

  await repo.save(s);
}

/**
 * ~4 meses de OS fechadas (só no ?dev), para o Dashboard Operacional ter o que
 * mostrar: divergência com motivo, relavagem e OS de Relavagem. Determinístico.
 * OS que o domínio recusar (combinação inválida) é só pulada.
 */
function historicoDemo(entrada: Snapshot): Snapshot {
  let s = entrada;
  let semente = 20261003;
  const sorteio = (): number => {
    semente = (semente * 1103515245 + 12345) % 2147483648;
    return semente / 2147483648;
  };
  const entre = (a: number, b: number): number => a + Math.floor(sorteio() * (b - a + 1));
  s = criarCliente(s, {
    nome: "Grand Atlântico",
    endereco: "Av. Beira-Mar, 300",
    contato: "(48) 3333-0000",
    codigo: "gat",
    tipo: "Hotel",
    baseFaturamento: "saida",
    cicloFechamento: { tipo: "mensal" },
    devolucaoNoMesmoDia: false,
    diasVencimento: [],
  });
  const tipos = s.tiposPeca.filter((t) => !t.nome.includes("precificar"));
  const motivos = s.listas.motivosDivergencia;
  const clientes: [string, number, number][] = [
    [s.clientes.find((c) => c.codigo === "HCP")!.id, 1.3, 0.8],
    [s.clientes.find((c) => c.codigo === "PVM")!.id, 0.5, 2.4],
    [s.clientes.find((c) => c.codigo === "GAT")!.id, 2.0, 1.0],
  ];
  let os = 900;
  const hoje = hojeISO();
  for (const [clienteId, tamanho, diverge] of clientes) {
    for (let d = 120; d >= 4; d -= entre(3, 5)) {
      const coleta = somarDias(hoje, -d);
      const relavagem = sorteio() < 0.06;
      try {
        s = lancarLote(s, {
          clienteId, os: os++, coletaData: coleta, coletaHora: "09:00", paradaId: null,
          sacos: null, urgente: false, relavagem, previstoManual: null,
        });
        const id = s.lotes[s.lotes.length - 1]!.id;
        const linhas = [];
        for (const t of tipos) {
          if (sorteio() < 0.2) continue;
          const recebida = relavagem ? entre(1, 5) : Math.round(tamanho * entre(15, 40));
          s = definirRecebida(s, id, t.id, recebida);
          const erro = !relavagem && sorteio() < 0.08 * diverge ? entre(1, 3) * (sorteio() < 0.5 ? -1 : 1) : 0;
          const conferida = Math.max(0, recebida + erro);
          const relavar = !relavagem && sorteio() < 0.12 ? Math.min(conferida, entre(1, 4)) : 0;
          linhas.push({
            tipoId: t.id,
            devolvida: conferida - relavar,
            retidas: relavar > 0 ? [{ motivo: "Relavagem", quantidade: relavar }] : [],
            ...(erro !== 0 ? { conferida, conferidaMotivo: motivos[entre(0, motivos.length - 1)]! } : {}),
          });
        }
        s = registrarDevolucao(s, id, { data: somarDias(coleta, entre(1, 3)), hora: "15:00", linhas });
      } catch {
        /* combinação recusada pelo domínio: pula esta OS */
      }
    }
  }
  return s;
}

/** Roteador de hash da montagem anterior — removido antes de remontar. */
let rotaAtual: (() => void) | null = null;

/** Descarta o roteador da casca anterior (a tela dela já não existe). */
function soltarRota(): void {
  if (rotaAtual) window.removeEventListener("hashchange", rotaAtual);
  rotaAtual = null;
}

/** Volta para a tela de login; ao entrar, o app começa no Dashboard. */
function mostrarLogin(): void {
  soltarRota();
  telaLogin(root, () => void bootHospedado(true));
}

/** Monta a casca (menu + topo + roteamento por hash). Comum aos dois modos. */
function montarApp(
  store: ReturnType<typeof createStore>,
  extra: { onBackup: () => void; onLogout?: () => void; usuarioNome?: string },
): Shell {
  // Sem sessão (modo local, ou `eu` ainda não resolvido): acesso total, como
  // sempre — Permissão de Módulo é um recurso do modo hospedado (issue #71).
  const uid = euIdAtual();
  const permiteModulo = uid
    ? (modulo: Modulo) => podeLerModulo(store.get(), uid, modulo)
    : undefined;

  const shell = mountShell(root, {
    onNavigate: (id) => {
      location.hash = `#/${id}`;
    },
    onBackup: extra.onBackup,
    ...(extra.onLogout ? { onLogout: extra.onLogout } : {}),
    ...(extra.usuarioNome ? { usuarioNome: extra.usuarioNome } : {}),
    ...(permiteModulo ? { permiteModulo } : {}),
    renderScreen: (id, container) => {
      const renderer = SCREENS[id];
      if (!renderer) return false;
      const limpar = renderer(container, store);
      return typeof limpar === "function" ? limpar : true;
    },
  });

  const screens = new Set(allScreenIds(permiteModulo));
  const route = (): void => {
    const inicial = telaInicial(permiteModulo);
    const id = location.hash.replace(/^#\/?/, "").split(/[/?]/)[0] || inicial;
    shell.showScreen(screens.has(id) ? id : inicial);
  };
  // no modo hospedado o boot pode remontar (logout → login); tira o anterior
  soltarRota();
  rotaAtual = route;
  window.addEventListener("hashchange", route);
  route();
  return shell;
}

async function startApp(db: Database, workspace: Workspace): Promise<void> {
  let shell: Shell | undefined;
  const repo = createRepository(db, async (bytes) => {
    await workspace.write(bytes);
  });

  await seedDefaults(repo);
  const store = createStore(repo.load(), repo, { ok: toastOk, erro: toastErro });

  shell = montarApp(store, {
    onBackup: () => {
      void workspace.saveBackup(db.export()).catch((e) => toastErro(`Backup falhou: ${e}`));
    },
  });
}

async function bootHospedado(vindoDoLogin = false): Promise<void> {
  gate("Carregando…", []);
  let estado;
  try {
    estado = await apiSnapshot();
  } catch (e) {
    gate(`Erro ao falar com o servidor: ${e}`, [
      { label: "Tentar de Novo", run: () => void bootHospedado(vindoDoLogin) },
    ]);
    return;
  }
  if (!estado) {
    mostrarLogin();
    return;
  }
  if (vindoDoLogin) location.hash = "";
  const eu = await apiEu(); // usuário logado: nome no topo, id pra tela de Usuários

  const repoRemoto: Repository = {
    load: () => estado,
    save: async () => {},
    exportBytes: () => new Uint8Array(),
  };
  const store = createStore(estado, repoRemoto, { ok: toastOk, erro: toastErro }, apiExecutar);
  montarApp(store, {
    ...(eu?.nome ? { usuarioNome: eu.nome } : {}),
    onBackup: () => {
      // baixa o arquivo .db direto do servidor (sem abrir aba)
      const a = document.createElement("a");
      a.href = "/api/db";
      a.download = "";
      a.click();
    },
    onLogout: () => {
      void apiLogout().finally(mostrarLogin);
    },
  });
}

/**
 * Registra o Service Worker mínimo (issue #72) — só instalabilidade, sem
 * cache. Nunca trava o boot: erro de registro (navegador sem suporte, sem
 * HTTPS) só fica no console.
 */
function registrarServiceWorker(): void {
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker.register("sw.js").catch((e: unknown) => {
    console.warn("Service Worker não registrado:", e);
  });
}

async function boot(): Promise<void> {
  if (__HOSTED__) {
    registrarServiceWorker();
    await bootHospedado();
    return;
  }

  const SQL = await loadSqlJs();

  // Modo de desenvolvimento: ?dev roda com um banco em memória, sem arquivo.
  if (new URLSearchParams(location.search).has("dev")) {
    const db = new SQL.Database();
    migrate(db);
    const fakeWorkspace: Workspace = {
      name: "dev.db",
      read: async () => new Uint8Array(),
      write: async () => {},
      saveBackup: async () => "dev-backup.db",
    };
    const semente = createRepository(db, async () => {});
    await seedDefaults(semente);
    await seedDemo(semente);
    await startApp(db, fakeWorkspace);
    return;
  }

  if (!LocalWorkspace.supported()) {
    gate(
      "Este navegador não suporta a File System Access API. Use o Microsoft Edge ou o Chrome no computador.",
      [],
    );
    return;
  }

  const openFrom = async (workspace: Workspace): Promise<void> => {
    const bytes = await workspace.read();
    const db = new SQL.Database(bytes.length ? bytes : undefined);
    migrate(db);
    await startApp(db, workspace);
  };

  const criarNovo = {
    label: "Criar Novo Banco de Dados",
    run: () => {
      void (async () => {
        const workspace = await LocalWorkspace.createNew();
        await openFrom(workspace);
      })();
    },
  };
  const abrirOutro = {
    label: "Abrir Banco Existente",
    run: () => {
      void (async () => {
        const workspace = await LocalWorkspace.openExisting();
        await openFrom(workspace);
      })();
    },
  };

  // Tenta reabrir o último arquivo.
  const remembered = await LocalWorkspace.getRemembered();
  if (remembered) {
    if (remembered.isGranted) {
      try {
        const workspace = await remembered.reopen();
        if (workspace) {
          await openFrom(workspace);
          return;
        }
      } catch {
        /* cai no gate abaixo */
      }
    }

    gate("Reabra o banco de dados da lavanderia para continuar.", [
      {
        label: `Reabrir “${remembered.name}”`,
        run: () => {
          void (async () => {
            const workspace = await remembered.reopen();
            if (workspace) {
              await openFrom(workspace);
            }
          })();
        },
      },
      criarNovo,
      abrirOutro,
    ]);
    return;
  }

  gate("Escolha o banco de dados da lavanderia para começar.", [
    criarNovo,
    abrirOutro,
  ]);
}

void boot().catch((e) => gate(`Erro ao iniciar: ${e}`, []));
